import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Package, PackageCheck, Search, Filter } from 'lucide-react';
import { motion } from 'framer-motion';

const OrderDetails = () => {
  const [view, setView] = useState('current');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const user = JSON.parse(localStorage.getItem('user'));
  const email = user?.email;

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await fetch('https://citi-mobile-app.onrender.com/saleagent/fetch-transactions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const data = await response.json();
        setOrders(data.transactions);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch orders');
        setLoading(false);
      }
    };

    if (email) fetchTransactions();
  }, [email]);

  const currentOrders = orders.filter(order => 
    order.deliverystatus === 'processing' &&
    (order.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     order._id?.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  const pastOrders = orders.filter(order => 
    order.deliverystatus === 'delivered' &&
    (order.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     order._id?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen pr-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen pr-96">
        <div className="bg-red-50 p-4 rounded-lg text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8 ml-64"> {/* Changed pl-96 to pr-96 */}
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Order Management</h1>
          <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name or order ID..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* Toggle Buttons */}
            <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setView('current')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  view === 'current'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Package size={20} />
                <span className="hidden sm:inline">Current Orders</span>
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setView('past')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  view === 'past'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <PackageCheck size={20} />
                <span className="hidden sm:inline">Past Orders</span>
              </motion.button>
            </div>
          </div>
        </div>

        {/* Orders Grid/List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {((view === 'current' ? currentOrders : pastOrders) || []).map((order) => (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              key={order._id}
              className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 truncate">{order.fullname}</h3>
                    <p className="text-sm text-gray-500">Order #{order._id.slice(-6)}</p>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      order.deliverystatus === 'processing'
                        ? 'bg-orange-100 text-orange-600'
                        : 'bg-green-100 text-green-600'
                    }`}
                  >
                    {order.deliverystatus}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Date:</span>
                    <span className="text-gray-900">{new Date(order.date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Amount:</span>
                    <span className="text-gray-900">${order.totalAmount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Payment:</span>
                    <span
                      className={`${
                        order.paymentstatus === 'Paid'
                          ? 'text-green-600'
                          : 'text-yellow-600'
                      }`}
                    >
                      {order.paymentstatus}
                    </span>
                  </div>
                </div>
              </div>
              
              <Link
                to={`/OrderDetails/SingleOrderDetails/${order._id}`}
                className="block bg-gray-50 px-4 py-3 text-sm text-blue-600 hover:bg-gray-100 transition-colors border-t border-gray-100"
              >
                <div className="flex items-center justify-between">
                  <span>View Details</span>
                  <ChevronRight size={16} />
                </div>
              </Link>
            </motion.div>
          ))}

          {/* Empty State */}
          {((view === 'current' && currentOrders.length === 0) ||
            (view === 'past' && pastOrders.length === 0)) && (
            <div className="col-span-full flex flex-col items-center justify-center py-12 px-4">
              <div className="bg-gray-100 rounded-full p-3 mb-4">
                {view === 'current' ? (
                  <Package size={24} className="text-gray-400" />
                ) : (
                  <PackageCheck size={24} className="text-gray-400" />
                )}
              </div>
              <h3 className="text-gray-900 font-medium mb-1">No orders found</h3>
              <p className="text-gray-500 text-sm text-center">
                {view === 'current'
                  ? "You don't have any current orders at the moment"
                  : "You don't have any past orders to display"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;